import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, Output } from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { APP_ICONS } from 'src/app/icons/icon-provider';

/** One contact row item (location / phone / email on candidate side,
 *  or location / rate / duration on job side). */
export interface CpHeaderMetaItem {
  /** Tabler icon name, e.g. `tablerMapPin`, `tablerPhone`, `tablerMail`. */
  icon: string;
  text: string;
  href?: string;
}

/** Small pill that sits beside the primary title
 *  (e.g. "⚡ Available" on candidate, "Active" on job). */
export interface CpHeaderBadge {
  label: string;
  tone: 'success' | 'warn' | 'neutral' | 'danger';
  /** Optional Tabler icon name. */
  icon?: string;
  /** Optional Unicode glyph (e.g. '⚡') — wins over `icon`. Mock parity:
   *  the wireframe badge uses the raw emoji, not an SVG icon. */
  glyph?: string;
}

/** Upgrade / featured marker shown beneath the title, visible on every
 *  slider tab (the header is sticky above the tab bar). Mirrors the
 *  detail pages: job → "Urgent Hire" + "Urgency ends in X days" countdown;
 *  candidate → "Hot" with no countdown. Hidden when null (not upgraded /
 *  upgrade expired). */
export interface CpUpgradeBadge {
  /** 'Urgent Hire' (job) or 'Hot' (candidate). */
  label: string;
  /** e.g. 'Urgency ends in 2 days' — job only; omitted on candidate. */
  countdown?: string;
}

/** Sticky slider header — avatar, name, ref id, meta row, match ring, close,
 *  action buttons (Download Resume / Chat / Share on candidate; Apply /
 *  Message / Save on job). All content is input-driven so the same
 *  component serves both `candidate-profile-slider` and `job-profile-slider`. */
@Component({
  selector: 'app-cp-header',
  standalone: true,
  imports: [CommonModule, NgIconComponent],
  providers: [provideIcons(APP_ICONS)],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './cp-header.component.html'
})
export class CpHeaderComponent {
  /** Used only for theming the match-ring accent. */
  @Input() direction: 'candidate' | 'job' = 'candidate';

  /** Avatar image URL. If absent OR the image fails to load, initials
   *  render instead. The setter resets `imgFailed` so a new candidate's
   *  photo gets a fresh attempt (the slider reuses this component across
   *  candidates). */
  @Input()
  set photoUrl(value: string | null) {
    this._photoUrl = value;
    this.imgFailed = false;
  }
  get photoUrl(): string | null {
    return this._photoUrl;
  }
  private _photoUrl: string | null = null;
  /** True once the avatar <img> emits an error — some rows carry a photo
   *  value that builds an invalid URL (e.g. the Candidates-Invited response),
   *  which would otherwise leave the avatar blank with no initials shown. */
  imgFailed = false;
  onImgError(): void {
    this.imgFailed = true;
  }
  @Input() initials: string = '?';
  @Input() avatarBg: string = '#EDE9FE';
  @Input() avatarFg: string = '#6D28D9';

  /** Primary title — candidate name or job title. */
  @Input() title: string = '';
  @Input() titleBadge: CpHeaderBadge | null = null;
  /** Upgrade marker (Urgent Hire / Hot) — shown across all slider tabs. */
  @Input() upgradeBadge: CpUpgradeBadge | null = null;

  /** Secondary line — job title (candidate) or client name (job). */
  @Input() subtitle: string = '';
  /** Reference id — CID / JID — rendered as a small code chip after subtitle. */
  @Input() refId: string = '';

  /** Meta row icons (location / phone / email OR location / rate / duration). */
  @Input() meta: CpHeaderMetaItem[] = [];

  /** 0–100 — rendered in the match ring. Hides when null/undefined. */
  @Input() matchScore: number | null = null;
  @Input() matchLabel: string = 'Match';

  /** When true, hides the match score and shows '-' instead (CEIPAL Free plan). */
  @Input() isCeipalFreePlan: boolean = false;

  /** Hides the match ring entirely — set alongside `hideAiInsights` on
   *  surfaces where AI matching doesn't apply (e.g. Resume Requests),
   *  since the ring's click opens the AI Insights tab. */
  @Input() hideMatchRing: boolean = false;

  /** Action buttons shown on the right. Each one emits `actionClick`
   *  with the action key. Typical keys: `download`, `chat`, `share` for
   *  candidate; `apply`, `message`, `save` for job.
   *  `disabled` blocks clicks; `spinner` fades the button and overlays a
   *  centered spinner (.jc-v3-btn-downloading) while keeping icon + label
   *  untouched (in-flight state, e.g. resume download in progress). */
  @Input() actions: Array<{ key: string; label: string; icon: string; tone?: 'primary' | 'outline'; disabled?: boolean; spinner?: boolean }> = [];

  @Output() matchRingClick = new EventEmitter<void>();
  @Output() closeClick = new EventEmitter<void>();
  @Output() actionClick = new EventEmitter<string>();

  /** trackBy for the action buttons *ngFor. The parent binds `[actions]` from
   *  a getter that returns a fresh array of new object literals every change
   *  detection tick; without a stable key Angular would destroy + recreate the
   *  button DOM on each tick (e.g. every mousemove), resetting `:hover` and
   *  restarting the CSS transition — the visible blink/flicker on hover.
   *  Keying by the stable `action.key` lets Angular reuse the existing nodes. */
  trackByActionKey(_index: number, action: { key: string }): string {
    return action.key;
  }

  // Match badge colouring is class-based in the template
  // (`[class.amber]="matchScore < 75"`) so it mirrors the
  // candidate-card pattern at candidate-card-table-view.component.html:401
  // and inherits the exact palette from `.jc-v3-cps-match-ring` /
  // `.jc-v3-cps-match-ring.amber` in job-center-v3.scss.
}
